import { useEffect, useMemo, useState } from "react";
import { api, API_BASE, formatMoney, openDownload } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, X, Wallet, ArrowDownLeft, ArrowUpRight, ArrowLeftRight,
  Building2, Calendar, Download, FileText, Filter, Receipt, TrendingUp,
  ClipboardCheck, AlertTriangle, CheckCircle2, Tag,
} from "lucide-react";

const TYPE_LBL = {
  in: "Giriş",
  out: "Çıkış",
  transfer_in: "Transfer (Geldi)",
  transfer_out: "Transfer (Gitti)",
};

const TYPE_COLOR = {
  in: "#10B981",
  out: "#EF4444",
  transfer_in: "#3B82F6",
  transfer_out: "#8B5CF6",
};

export default function Kasa() {
  const [kasalar, setKasalar] = useState([]);
  const [banks, setBanks] = useState([]);
  const [caris, setCaris] = useState([]);
  const [selected, setSelected] = useState(null);
  const [movements, setMovements] = useState([]);
  const [modal, setModal] = useState(null); // 'new-kasa' | 'movement' | 'transfer' | 'from-bank' | 'count' | 'cat-manage'
  const [form, setForm] = useState({});
  const today = new Date().toISOString().slice(0, 10);
  const [reportDate, setReportDate] = useState(today);
  const [reportMonth, setReportMonth] = useState(today.slice(0, 7));
  const [reportMode, setReportMode] = useState("daily"); // 'daily' | 'monthly'
  const [dailyReport, setDailyReport] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [counts, setCounts] = useState([]);
  const [categories, setCategories] = useState({ preset: [], user: [], all: [] });

  const loadKasalar = async () => {
    const { data } = await api.get("/kasa");
    setKasalar(data);
    if (!selected && data.length > 0) setSelected(data[0]);
    if (selected) {
      const updated = data.find(k => k.id === selected.id);
      if (updated) setSelected(updated);
    }
  };
  const loadBanks = async () => setBanks((await api.get("/bank-accounts")).data);
  const loadCaris = async () => setCaris((await api.get("/cari")).data);
  const loadMovements = async () => {
    if (!selected) return;
    const { data } = await api.get(`/kasa/${selected.id}/movements`);
    setMovements(data);
  };
  const loadReport = async () => {
    if (!selected) { setDailyReport(null); setMonthlyReport(null); setCounts([]); return; }
    try {
      const { data: dd } = await api.get(`/kasa/${selected.id}/daily-report`, { params: { date: reportDate } });
      setDailyReport(dd);
    } catch { setDailyReport(null); }
    try {
      const { data: mm } = await api.get(`/kasa/${selected.id}/monthly-report`, { params: { month: reportMonth } });
      setMonthlyReport(mm);
    } catch { setMonthlyReport(null); }
    try {
      const { data: cs } = await api.get(`/kasa/${selected.id}/counts`);
      setCounts(cs);
    } catch { setCounts([]); }
  };
  const loadCategories = async () => {
    try { const { data } = await api.get("/kasa/categories"); setCategories(data); }
    catch (err) { console.error("Kasa kategorileri yüklenemedi", err); }
  };

  useEffect(() => { loadKasalar(); loadBanks(); loadCaris(); loadCategories(); }, []);
  useEffect(() => { loadMovements(); loadReport(); /* eslint-disable-next-line */ }, [selected, reportDate, reportMonth]);

  const openNewKasa = () => { setForm({ name: "", currency: "TRY", location: "", description: "", color: "#F59E0B", opening_balance: "", opening_date: today }); setModal("new-kasa"); };
  const openMovement = (type) => { setForm({ type, amount: "", date: today, description: "", category: "", cari_id: "" }); setModal("movement"); };
  const openTransfer = () => { setForm({ amount: "", date: today, description: "", mode: "kasa", target_kasa_id: "", target_bank_id: "" }); setModal("transfer"); };
  const openFromBank = () => { setForm({ amount: "", date: today, description: "", target_bank_id: banks[0]?.id || "" }); setModal("from-bank"); };
  const openCount = () => {
    // Prefill counted_amount with current balance for convenience
    setForm({
      date: today,
      counted_amount: selected?.balance || 0,
      denominations: {}, // {"200":"","100":"",...}
      note: "",
      create_adjustment: false,
      show_denom: false,
    });
    setModal("count");
  };
  const openCatManage = () => { setForm({ new_cat: "" }); setModal("cat-manage"); };
  const close = () => setModal(null);

  const saveKasa = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.opening_balance) payload.opening_balance = Number(payload.opening_balance);
      else payload.opening_balance = 0;
      await api.post("/kasa", payload);
      toast.success("Kasa oluşturuldu");
      close(); loadKasalar();
    } catch { toast.error("Kayıt başarısız"); }
  };
  const saveMovement = async (e) => {
    e.preventDefault();
    if (!selected) return;
    try {
      await api.post(`/kasa/${selected.id}/movements`, { ...form, kasa_id: selected.id, amount: Number(form.amount), currency: selected.currency });
      toast.success(form.type === "in" ? "Giriş kaydedildi" : "Çıkış kaydedildi");
      close(); loadKasalar(); loadMovements(); loadReport();
    } catch { toast.error("Kayıt başarısız"); }
  };
  const saveTransfer = async (e) => {
    e.preventDefault();
    if (!selected) return;
    try {
      const payload = { amount: Number(form.amount), date: form.date, description: form.description };
      if (form.mode === "kasa") payload.target_kasa_id = form.target_kasa_id;
      else payload.target_bank_id = form.target_bank_id;
      await api.post(`/kasa/${selected.id}/transfer`, payload);
      toast.success("Transfer tamamlandı");
      close(); loadKasalar(); loadMovements(); loadReport();
    } catch { toast.error("Transfer başarısız"); }
  };
  const saveFromBank = async (e) => {
    e.preventDefault();
    if (!selected) return;
    try {
      await api.post(`/kasa/${selected.id}/deposit-from-bank`, {
        amount: Number(form.amount), date: form.date, description: form.description, target_bank_id: form.target_bank_id,
      });
      toast.success("Bankadan çekildi");
      close(); loadKasalar(); loadMovements(); loadReport();
    } catch { toast.error("Başarısız"); }
  };
  const saveCount = async (e) => {
    e.preventDefault();
    if (!selected) return;
    // Build denominations dict (drop empty)
    const denom = {};
    Object.entries(form.denominations || {}).forEach(([k, v]) => {
      const n = parseInt(v, 10);
      if (n && n > 0) denom[k] = n;
    });
    // If user entered denominations, recompute counted_amount from them
    let counted = Number(form.counted_amount);
    if (form.show_denom && Object.keys(denom).length > 0) {
      counted = Object.entries(denom).reduce((sum, [d, c]) => sum + parseFloat(d) * c, 0);
    }
    try {
      await api.post(`/kasa/${selected.id}/counts`, {
        date: form.date,
        counted_amount: counted,
        denominations: denom,
        note: form.note || "",
        create_adjustment: !!form.create_adjustment,
      });
      toast.success(form.create_adjustment ? "Sayım kaydedildi ve fark düzeltildi" : "Sayım kaydedildi");
      close(); loadKasalar(); loadMovements(); loadReport();
    } catch { toast.error("Sayım kaydedilemedi"); }
  };
  const delCount = async (cid) => {
    if (!window.confirm("Bu sayım kaydı silinsin mi? (Varsa düzeltme hareketi de geri alınır.)")) return;
    await api.delete(`/kasa/counts/${cid}`);
    loadKasalar(); loadMovements(); loadReport();
    toast.success("Sayım silindi");
  };
  const openCountPdf = (cid) => {
    openDownload(`/api/kasa/counts/${cid}/report.pdf`);
  };
  const addCategory = async () => {
    const name = (form.new_cat || "").trim();
    if (!name) return;
    try {
      await api.post("/kasa/categories", { name });
      toast.success("Kategori eklendi");
      setForm({ ...form, new_cat: "" });
      loadCategories();
    } catch { toast.error("Eklenemedi"); }
  };
  const delCategory = async (name) => {
    if (!window.confirm(`"${name}" kategorisi silinsin mi?`)) return;
    await api.delete(`/kasa/categories/${encodeURIComponent(name)}`);
    loadCategories();
  };
  const delKasa = async () => {
    if (!selected) return;
    if (!window.confirm(`${selected.name} silinsin mi? (Sadece hareketsiz kasalar silinebilir)`)) return;
    try { await api.delete(`/kasa/${selected.id}`); setSelected(null); loadKasalar(); toast.success("Silindi"); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };
  const delMovement = async (mid) => {
    if (!window.confirm("Bu hareket silinsin mi? Bakiye otomatik güncellenir.")) return;
    await api.delete(`/kasa/movements/${mid}`);
    loadKasalar(); loadMovements(); loadReport();
    toast.success("Silindi");
  };
  const downloadReport = (fmt) => {
    if (!selected) return;
    if (reportMode === "daily") {
      openDownload(`/api/kasa/${selected.id}/daily-report.${fmt}`, { date: reportDate });
    } else {
      openDownload(`/api/kasa/${selected.id}/monthly-report.pdf`, { month: reportMonth });
    }
  };

  const openReceipt = (mid) => {
    openDownload(`/api/kasa/movements/${mid}/receipt.pdf`);
  };

  const cariMap = useMemo(() => Object.fromEntries(caris.map(c => [c.id, c.name])), [caris]);
  const kasaMap = useMemo(() => Object.fromEntries(kasalar.map(k => [k.id, k.name])), [kasalar]);
  const bankMap = useMemo(() => Object.fromEntries(banks.map(b => [b.id, b.name])), [banks]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold">Finans</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 dark:text-slate-100 mt-1">Kasa Takibi</h1>
          <p className="text-sm text-slate-500 mt-1">Fiziksel nakit hareketleri, transferler, günlük raporlar</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="new-kasa" onClick={openNewKasa}
            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] shadow-lg shadow-[#1E2A9C]/20">
            <Plus className="w-4 h-4" /> Yeni Kasa
          </button>
        </div>
      </div>

      {/* Kasa cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kasalar.map(k => {
          const active = selected?.id === k.id;
          return (
            <button key={k.id} data-testid={`kasa-${k.id}`} onClick={() => setSelected(k)}
              className={`text-left p-5 rounded-2xl transition ${active ? "ring-2 ring-offset-2 dark:ring-offset-slate-900 ring-[#1E2A9C]" : ""}`}
              style={{ background: `linear-gradient(135deg, ${k.color}, ${k.color}CC)` }}>
              <div className="flex items-start justify-between text-white">
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Kasa · {k.currency}</div>
                  <div className="font-display text-lg font-extrabold mt-1">{k.name}</div>
                  {k.location && <div className="text-xs opacity-80 mt-0.5">{k.location}</div>}
                </div>
                <Wallet className="w-6 h-6 opacity-70" />
              </div>
              <div className="text-3xl font-extrabold text-white mt-4 font-mono-num">{formatMoney(k.balance, k.currency)}</div>
            </button>
          );
        })}
        {kasalar.length === 0 && (
          <div className="col-span-full p-12 rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-center text-slate-500">
            Henüz kasa yok. "Yeni Kasa" ile başlayın.
          </div>
        )}
      </div>

      {selected && (
        <>
          {/* Action bar */}
          <div className="flex flex-wrap gap-2 p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-2 mr-2 text-xs font-bold text-slate-500 uppercase tracking-widest">
              <Filter className="w-3.5 h-3.5" /> {selected.name}
            </div>
            <button data-testid="btn-in" onClick={() => openMovement("in")}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 font-bold text-sm hover:bg-emerald-200">
              <ArrowDownLeft className="w-4 h-4" /> Giriş
            </button>
            <button data-testid="btn-out" onClick={() => openMovement("out")}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 font-bold text-sm hover:bg-rose-200">
              <ArrowUpRight className="w-4 h-4" /> Çıkış
            </button>
            <button data-testid="btn-transfer" onClick={openTransfer}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-bold text-sm hover:bg-blue-200">
              <ArrowLeftRight className="w-4 h-4" /> Transfer
            </button>
            <button data-testid="btn-from-bank" onClick={openFromBank}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 font-bold text-sm hover:bg-violet-200">
              <Building2 className="w-4 h-4" /> Bankadan Çek
            </button>
            <button data-testid="btn-count" onClick={openCount}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 font-bold text-sm hover:bg-amber-200">
              <ClipboardCheck className="w-4 h-4" /> Sayım
            </button>
            <div className="flex-1" />
            <button data-testid="del-kasa" onClick={delKasa} title="Kasayı sil"
              className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Daily / Monthly report */}
          <div className="p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Kasa Raporu</div>
                <div className="font-display text-xl font-extrabold text-slate-900 dark:text-slate-100">
                  {selected.name} · {reportMode === "daily" ? reportDate : reportMonth}
                </div>
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <button data-testid="rep-daily" onClick={() => setReportMode("daily")}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold transition ${reportMode === "daily" ? "bg-white dark:bg-slate-900 text-[#1E2A9C] shadow-sm" : "text-slate-500"}`}>
                    <Calendar className="w-3.5 h-3.5" /> Günlük
                  </button>
                  <button data-testid="rep-monthly" onClick={() => setReportMode("monthly")}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold transition ${reportMode === "monthly" ? "bg-white dark:bg-slate-900 text-[#1E2A9C] shadow-sm" : "text-slate-500"}`}>
                    <TrendingUp className="w-3.5 h-3.5" /> Aylık
                  </button>
                </div>
                {reportMode === "daily" ? (
                  <input type="date" data-testid="report-date" value={reportDate} onChange={(e) => setReportDate(e.target.value)}
                    className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 font-mono-num" />
                ) : (
                  <input type="month" data-testid="report-month" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)}
                    className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 font-mono-num" />
                )}
                <button data-testid="dl-pdf" onClick={() => downloadReport("pdf")}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-bold hover:border-[#1E2A9C]">
                  <FileText className="w-3.5 h-3.5" /> PDF
                </button>
                {reportMode === "daily" && (
                  <button data-testid="dl-xlsx" onClick={() => downloadReport("xlsx")}
                    className="flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-bold hover:border-[#1E2A9C]">
                    <Download className="w-3.5 h-3.5" /> Excel
                  </button>
                )}
              </div>
            </div>
            {reportMode === "daily" && dailyReport && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Gün Başı" value={dailyReport.opening} cur={selected.currency} />
                <StatCard label="Toplam Giriş" value={dailyReport.total_in} cur={selected.currency} color="emerald" prefix="+" />
                <StatCard label="Toplam Çıkış" value={dailyReport.total_out} cur={selected.currency} color="rose" prefix="-" />
                <StatCard label="Gün Sonu" value={dailyReport.closing} cur={selected.currency} highlight />
              </div>
            )}
            {reportMode === "monthly" && monthlyReport && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  <StatCard label="Ay Başı" value={monthlyReport.opening} cur={selected.currency} />
                  <StatCard label="Toplam Giriş" value={monthlyReport.total_in} cur={selected.currency} color="emerald" prefix="+" />
                  <StatCard label="Toplam Çıkış" value={monthlyReport.total_out} cur={selected.currency} color="rose" prefix="-" />
                  <StatCard label="Ay Sonu" value={monthlyReport.closing} cur={selected.currency} highlight />
                </div>
                <div className="max-h-[280px] overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg" data-testid="monthly-days">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0 text-xs uppercase font-bold text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Tarih</th>
                        <th className="px-3 py-2 text-center">Hareket</th>
                        <th className="px-3 py-2 text-right">Giriş</th>
                        <th className="px-3 py-2 text-right">Çıkış</th>
                        <th className="px-3 py-2 text-right">Gün Sonu Bakiye</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyReport.days.filter(d => d.count > 0).map(d => (
                        <tr key={d.date} className="border-t border-slate-100 dark:border-slate-800">
                          <td className="px-3 py-2 font-mono-num">{d.date}</td>
                          <td className="px-3 py-2 text-center text-slate-500">{d.count}</td>
                          <td className="px-3 py-2 text-right font-mono-num text-emerald-600">{d.in > 0 ? "+ " + formatMoney(d.in, selected.currency) : "-"}</td>
                          <td className="px-3 py-2 text-right font-mono-num text-rose-600">{d.out > 0 ? "- " + formatMoney(d.out, selected.currency) : "-"}</td>
                          <td className="px-3 py-2 text-right font-mono-num font-bold">{formatMoney(d.closing, selected.currency)}</td>
                        </tr>
                      ))}
                      {monthlyReport.days.filter(d => d.count > 0).length === 0 && (
                        <tr><td colSpan="5" className="text-center py-6 text-slate-400 text-xs">Bu ay hareket yok</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {monthlyReport.categories && monthlyReport.categories.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {monthlyReport.categories.slice(0, 6).map(c => {
                      const net = c.in - c.out;
                      return (
                        <div key={c.name} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{c.name}</div>
                          <div className={`text-sm font-bold font-mono-num mt-1 ${net >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                            {net >= 0 ? "+" : ""}{formatMoney(net, selected.currency)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sayım Geçmişi */}
          {counts.length > 0 && (
            <div className="p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800" data-testid="counts-section">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
                    <ClipboardCheck className="w-3 h-3" /> Sayım Geçmişi
                  </div>
                  <div className="font-display text-xl font-extrabold text-slate-900 dark:text-slate-100">{counts.length} sayım kaydı</div>
                </div>
              </div>
              <div className="space-y-2 max-h-[320px] overflow-y-auto">
                {counts.map(c => {
                  const variance = c.variance || 0;
                  const isMatch = Math.abs(variance) < 0.01;
                  const isOver = variance > 0;
                  return (
                    <div key={c.id} data-testid={`count-${c.id}`}
                      className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-slate-400 group"
                      style={{ borderLeftWidth: 4, borderLeftColor: isMatch ? "#10B981" : isOver ? "#3B82F6" : "#EF4444" }}>
                      <div className="w-10 h-10 rounded-full grid place-items-center flex-shrink-0"
                        style={{ backgroundColor: (isMatch ? "#10B981" : isOver ? "#3B82F6" : "#EF4444") + "20",
                                 color: isMatch ? "#10B981" : isOver ? "#3B82F6" : "#EF4444" }}>
                        {isMatch ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Sayım · {c.date}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${isMatch ? "bg-emerald-100 text-emerald-700" : isOver ? "bg-blue-100 text-blue-700" : "bg-rose-100 text-rose-700"}`}>
                            {isMatch ? "TAM" : isOver ? "FAZLA" : "EKSİK"}
                          </span>
                          {c.adjustment_movement_id && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">Düzeltildi</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex gap-3 flex-wrap font-mono-num">
                          <span>Sistem: {formatMoney(c.system_balance, selected.currency)}</span>
                          <span>Sayılan: {formatMoney(c.counted_amount, selected.currency)}</span>
                          {c.note && <span className="text-slate-400">· {c.note}</span>}
                        </div>
                      </div>
                      <div className={`font-mono-num text-lg font-extrabold ${isMatch ? "text-emerald-600" : isOver ? "text-blue-600" : "text-rose-600"}`}>
                        {variance > 0 ? "+" : ""}{formatMoney(variance, selected.currency)}
                      </div>
                      <button onClick={() => openCountPdf(c.id)} data-testid={`count-pdf-${c.id}`} title="PDF"
                        className="p-1.5 text-slate-400 hover:text-[#1E2A9C]">
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => delCount(c.id)}
                        className="opacity-0 group-hover:opacity-100 transition p-1.5 text-slate-400 hover:text-rose-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Movement history */}
          <div className="p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Hareketler</div>
                <div className="font-display text-xl font-extrabold text-slate-900 dark:text-slate-100">{movements.length} kayıt</div>
              </div>
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {movements.length === 0 && <div className="text-center py-8 text-sm text-slate-400">Bu kasa için hareket yok</div>}
              {movements.map(m => {
                const color = TYPE_COLOR[m.type] || "#64748B";
                const isIn = m.type === "in" || m.type === "transfer_in";
                return (
                  <div key={m.id} data-testid={`mv-${m.id}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-slate-400 group"
                       style={{ borderLeftWidth: 4, borderLeftColor: color }}>
                    <div className="w-10 h-10 rounded-full grid place-items-center flex-shrink-0" style={{ backgroundColor: color + "20", color }}>
                      {m.type === "in" && <ArrowDownLeft className="w-4 h-4" />}
                      {m.type === "out" && <ArrowUpRight className="w-4 h-4" />}
                      {m.type === "transfer_in" && <ArrowDownLeft className="w-4 h-4" />}
                      {m.type === "transfer_out" && <ArrowUpRight className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color }}>{TYPE_LBL[m.type]}</span>
                        <span className="text-xs text-slate-400 font-mono-num">{m.date}</span>
                        {m.category && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 font-bold">{m.category}</span>}
                      </div>
                      <div className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate">{m.description || "-"}</div>
                      {(m.counter_kasa_id || m.counter_bank_id) && (
                        <div className="text-[10px] text-slate-500">
                          {m.counter_kasa_id && <>↔ {kasaMap[m.counter_kasa_id] || m.counter_kasa_id}</>}
                          {m.counter_bank_id && <>↔ {bankMap[m.counter_bank_id] || m.counter_bank_id}</>}
                          {m.reference && <> · <span className="font-mono-num">{m.reference}</span></>}
                        </div>
                      )}
                    </div>
                    <div className={`font-mono-num text-lg font-extrabold ${isIn ? "text-emerald-600" : "text-rose-600"}`}>
                      {isIn ? "+" : "-"} {formatMoney(m.amount, m.currency)}
                    </div>
                    <button onClick={() => openReceipt(m.id)} data-testid={`receipt-${m.id}`} title="Fiş yazdır"
                      className="p-1.5 text-slate-400 hover:text-[#1E2A9C]">
                      <Receipt className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => delMovement(m.id)} className="opacity-0 group-hover:opacity-100 transition p-1.5 text-slate-400 hover:text-rose-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {modal === "new-kasa" && (
        <Modal title="Yeni Kasa" onClose={close}>
          <form onSubmit={saveKasa} className="space-y-4">
            <Input label="Kasa Adı" required value={form.name} onChange={v => setForm({ ...form, name: v })} testId="k-name" />
            <div className="grid grid-cols-2 gap-3">
              <Select label="Para Birimi" value={form.currency} onChange={v => setForm({ ...form, currency: v })} options={[["TRY","TRY"],["USD","USD"],["EUR","EUR"]]} testId="k-cur" />
              <Input label="Renk" type="color" value={form.color} onChange={v => setForm({ ...form, color: v })} />
            </div>
            <Input label="Konum" value={form.location} onChange={v => setForm({ ...form, location: v })} placeholder="Ofis, Şube 1, Kasa 2..." />
            <div className="grid grid-cols-2 gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <Input label="Açılış / Devir Bakiyesi (opsiyonel)" type="number" step="0.01"
                value={form.opening_balance} onChange={v => setForm({ ...form, opening_balance: v })}
                placeholder="0.00" testId="k-opening" />
              <Input label="Açılış Tarihi" type="date" value={form.opening_date} onChange={v => setForm({ ...form, opening_date: v })} testId="k-opening-date" />
            </div>
            <Input label="Açıklama" value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <SubmitBtn>Kasa Oluştur</SubmitBtn>
          </form>
        </Modal>
      )}

      {modal === "movement" && selected && (
        <Modal title={form.type === "in" ? `${selected.name} — Nakit Giriş` : `${selected.name} — Nakit Çıkış`} onClose={close}>
          <form onSubmit={saveMovement} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Input label={`Tutar (${selected.currency})`} type="number" step="0.01" required value={form.amount} onChange={v => setForm({ ...form, amount: v })} testId="mv-amount" />
              <Input label="Tarih" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
            </div>
            <Input label="Açıklama" value={form.description} onChange={v => setForm({ ...form, description: v })} placeholder="Ör: Nakit tahsilat müşteri X" testId="mv-desc" />
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase font-bold text-slate-500 tracking-widest flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Kategori
                </label>
                <button type="button" onClick={openCatManage} data-testid="manage-cats" className="text-[10px] uppercase font-bold text-[#1E2A9C] hover:underline">
                  Yönet
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2 max-h-[110px] overflow-y-auto" data-testid="cat-chips">
                {categories.all.map(c => (
                  <button key={c} type="button" data-testid={`chip-${c}`}
                    onClick={() => setForm({ ...form, category: form.category === c ? "" : c })}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition ${
                      form.category === c
                        ? "bg-[#1E2A9C] text-white shadow-sm"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
                    }`}>
                    {c}
                  </button>
                ))}
              </div>
              <input
                type="text" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="mv-cat-input"
                placeholder="Veya özel kategori yaz..."
                className="w-full mt-2 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg dark:bg-slate-800 dark:text-slate-100" />
            </div>
            <Select label="Cari (Opsiyonel)" value={form.cari_id} onChange={v => setForm({ ...form, cari_id: v })}
              options={[["", "— Yok —"], ...caris.map(c => [c.id, c.name])]} />
            <SubmitBtn variant={form.type === "in" ? "emerald" : "rose"}>{form.type === "in" ? "Girişi Kaydet" : "Çıkışı Kaydet"}</SubmitBtn>
          </form>
        </Modal>
      )}

      {modal === "transfer" && selected && (
        <Modal title={`${selected.name} — Transfer`} onClose={close}>
          <form onSubmit={saveTransfer} className="space-y-4">
            <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <button type="button" onClick={() => setForm({ ...form, mode: "kasa" })}
                data-testid="tx-mode-kasa"
                className={`px-3 py-2 rounded font-bold text-sm ${form.mode === "kasa" ? "bg-white dark:bg-slate-900 text-[#1E2A9C] shadow-sm" : "text-slate-500"}`}>
                Kasa → Kasa
              </button>
              <button type="button" onClick={() => setForm({ ...form, mode: "bank" })}
                data-testid="tx-mode-bank"
                className={`px-3 py-2 rounded font-bold text-sm ${form.mode === "bank" ? "bg-white dark:bg-slate-900 text-[#1E2A9C] shadow-sm" : "text-slate-500"}`}>
                Kasa → Banka (Yatır)
              </button>
            </div>
            {form.mode === "kasa" ? (
              <Select label="Hedef Kasa" required value={form.target_kasa_id} onChange={v => setForm({ ...form, target_kasa_id: v })}
                options={[["", "— Seç —"], ...kasalar.filter(k => k.id !== selected.id).map(k => [k.id, `${k.name} · ${k.currency}`])]} testId="tx-target-kasa" />
            ) : (
              <Select label="Hedef Banka" required value={form.target_bank_id} onChange={v => setForm({ ...form, target_bank_id: v })}
                options={[["", "— Seç —"], ...banks.filter(b => b.currency === selected.currency).map(b => [b.id, `${b.name} · ${b.currency}`])]} testId="tx-target-bank" />
            )}
            <div className="grid grid-cols-2 gap-3">
              <Input label={`Tutar (${selected.currency})`} type="number" step="0.01" required value={form.amount} onChange={v => setForm({ ...form, amount: v })} testId="tx-amount" />
              <Input label="Tarih" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
            </div>
            <Input label="Açıklama" value={form.description} onChange={v => setForm({ ...form, description: v })} placeholder="Transfer notu" />
            <SubmitBtn variant="blue">Transferi Onayla</SubmitBtn>
          </form>
        </Modal>
      )}

      {modal === "from-bank" && selected && (
        <Modal title={`Bankadan ${selected.name}'e Para Çek`} onClose={close}>
          <form onSubmit={saveFromBank} className="space-y-4">
            <Select label="Kaynak Banka" required value={form.target_bank_id} onChange={v => setForm({ ...form, target_bank_id: v })}
              options={[["", "— Seç —"], ...banks.filter(b => b.currency === selected.currency).map(b => [b.id, `${b.name} · ${formatMoney(b.balance, b.currency)}`])]} testId="fb-bank" />
            <div className="grid grid-cols-2 gap-3">
              <Input label={`Tutar (${selected.currency})`} type="number" step="0.01" required value={form.amount} onChange={v => setForm({ ...form, amount: v })} testId="fb-amount" />
              <Input label="Tarih" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
            </div>
            <Input label="Açıklama" value={form.description} onChange={v => setForm({ ...form, description: v })} placeholder="ATM çekimi, banka ziyareti..." />
            <SubmitBtn variant="violet">Para Çek</SubmitBtn>
          </form>
        </Modal>
      )}

      {modal === "count" && selected && (() => {
        const denomTotal = Object.entries(form.denominations || {}).reduce((s, [d, c]) => {
          const n = parseInt(c, 10); return s + (n > 0 ? parseFloat(d) * n : 0);
        }, 0);
        const effectiveCounted = form.show_denom && denomTotal > 0 ? denomTotal : Number(form.counted_amount || 0);
        const variance = effectiveCounted - Number(selected.balance || 0);
        const denomList = selected.currency === "TRY"
          ? [200, 100, 50, 20, 10, 5, 1, 0.5, 0.25, 0.10, 0.05, 0.01]
          : selected.currency === "USD"
          ? [100, 50, 20, 10, 5, 2, 1, 0.5, 0.25, 0.10, 0.05, 0.01]
          : [500, 200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.20, 0.10, 0.05, 0.02, 0.01];
        return (
          <Modal title={`${selected.name} — Kasa Sayımı`} onClose={close}>
            <form onSubmit={saveCount} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Input label="Sayım Tarihi" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} testId="cnt-date" />
                <div>
                  <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Sistem Bakiyesi</label>
                  <div className="mt-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 font-mono-num font-bold" data-testid="cnt-sys-bal">
                    {formatMoney(selected.balance, selected.currency)}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Sayım Yöntemi:</label>
                <button type="button" onClick={() => setForm({ ...form, show_denom: false })}
                  data-testid="cnt-mode-total"
                  className={`px-3 py-1 rounded text-xs font-bold ${!form.show_denom ? "bg-[#1E2A9C] text-white" : "bg-slate-100 text-slate-600"}`}>
                  Toplam Tutar
                </button>
                <button type="button" onClick={() => setForm({ ...form, show_denom: true })}
                  data-testid="cnt-mode-denom"
                  className={`px-3 py-1 rounded text-xs font-bold ${form.show_denom ? "bg-[#1E2A9C] text-white" : "bg-slate-100 text-slate-600"}`}>
                  Kupür Kupür
                </button>
              </div>

              {!form.show_denom ? (
                <Input label={`Sayılan Tutar (${selected.currency})`} type="number" step="0.01" required
                  value={form.counted_amount} onChange={v => setForm({ ...form, counted_amount: v })} testId="cnt-amount" />
              ) : (
                <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 max-h-[260px] overflow-y-auto">
                  <div className="grid grid-cols-3 gap-2">
                    {denomList.map(d => {
                      const key = String(d);
                      const count = (form.denominations || {})[key] || "";
                      const subtotal = (parseInt(count, 10) || 0) * d;
                      return (
                        <div key={key} className="flex flex-col gap-0.5 p-2 border border-slate-100 dark:border-slate-800 rounded">
                          <div className="text-[10px] font-mono-num font-bold text-slate-500">{formatMoney(d, selected.currency)}</div>
                          <input type="number" min="0" placeholder="0"
                            data-testid={`denom-${key}`}
                            value={count}
                            onChange={(e) => setForm({ ...form, denominations: { ...(form.denominations || {}), [key]: e.target.value } })}
                            className="w-full px-2 py-1 text-sm border border-slate-200 dark:border-slate-700 rounded dark:bg-slate-800 font-mono-num" />
                          {subtotal > 0 && (
                            <div className="text-[10px] font-mono-num text-emerald-600 font-bold text-right">= {formatMoney(subtotal, selected.currency)}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Kupür Toplamı</span>
                    <span className="font-mono-num font-extrabold text-[#1E2A9C]" data-testid="denom-total">{formatMoney(denomTotal, selected.currency)}</span>
                  </div>
                </div>
              )}

              <div className={`p-4 rounded-lg border-2 ${Math.abs(variance) < 0.01 ? "bg-emerald-50 border-emerald-300" : variance > 0 ? "bg-blue-50 border-blue-300" : "bg-rose-50 border-rose-300"}`} data-testid="cnt-variance">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-600 flex items-center gap-1">
                    {Math.abs(variance) < 0.01 ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
                    Fark {Math.abs(variance) < 0.01 ? "(Tam)" : variance > 0 ? "(Fazla)" : "(Eksik)"}
                  </span>
                  <span className={`font-mono-num text-2xl font-extrabold ${Math.abs(variance) < 0.01 ? "text-emerald-600" : variance > 0 ? "text-blue-600" : "text-rose-600"}`}>
                    {variance > 0 ? "+" : ""}{formatMoney(variance, selected.currency)}
                  </span>
                </div>
              </div>

              <Input label="Not" value={form.note} onChange={v => setForm({ ...form, note: v })} placeholder="Sayım notu, sorumlu kişi..." testId="cnt-note" />

              {Math.abs(variance) >= 0.01 && (
                <label className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-300 cursor-pointer" data-testid="cnt-adjust-label">
                  <input type="checkbox" checked={!!form.create_adjustment} data-testid="cnt-adjust"
                    onChange={(e) => setForm({ ...form, create_adjustment: e.target.checked })} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-amber-900">Farkı otomatik hareket olarak kaydet</div>
                    <div className="text-xs text-amber-700 mt-0.5">
                      "Sayım Farkı" kategorisinde bir {variance > 0 ? "GİRİŞ" : "ÇIKIŞ"} hareketi oluşturulur, sistem bakiyesi sayılan tutara eşitlenir.
                    </div>
                  </div>
                </label>
              )}

              <SubmitBtn>Sayımı Kaydet</SubmitBtn>
            </form>
          </Modal>
        );
      })()}

      {modal === "cat-manage" && (
        <Modal title="Kasa Kategorileri" onClose={close}>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Kullanıcı Kategorileri</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {categories.user.length === 0 && <span className="text-xs text-slate-400">Henüz özel kategori yok</span>}
                {categories.user.map(c => (
                  <span key={c} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-testid={`user-cat-${c}`}>
                    {c}
                    <button type="button" onClick={() => delCategory(c)} className="hover:text-rose-600">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Yeni Kategori Ekle</label>
              <div className="flex gap-2 mt-2">
                <input type="text" value={form.new_cat || ""} data-testid="new-cat-input"
                  onChange={(e) => setForm({ ...form, new_cat: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }}
                  placeholder="Örn: Turizm, Bahis, Kripto..."
                  className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800" />
                <button type="button" onClick={addCategory} data-testid="add-cat"
                  className="px-4 py-2 rounded-lg bg-[#1E2A9C] text-white font-bold text-sm">
                  Ekle
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Ön Tanımlı (Silinemez)</label>
              <div className="flex flex-wrap gap-1.5 mt-2 max-h-[140px] overflow-y-auto">
                {categories.preset.map(c => (
                  <span key={c} className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-500">{c}</span>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatCard({ label, value, cur, color, prefix, highlight }) {
  const cls = color === "emerald" ? "text-emerald-600" : color === "rose" ? "text-rose-600" : highlight ? "text-[#1E2A9C]" : "text-slate-900 dark:text-slate-100";
  return (
    <div className={`p-4 rounded-xl ${highlight ? "border-2 border-[#1E2A9C]" : "border border-slate-200 dark:border-slate-700"}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className={`font-mono-num text-2xl font-extrabold mt-1 ${cls}`}>
        {prefix && <span className="mr-0.5">{prefix}</span>}{formatMoney(value, cur)}
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 grid place-items-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg p-8 relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
        <div className="font-display text-2xl font-extrabold mb-6 text-slate-900 dark:text-slate-100">{title}</div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, placeholder, step, testId }) {
  return (
    <div>
      <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">{label}</label>
      <input type={type} step={step} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} data-testid={testId}
        className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100" />
    </div>
  );
}

function Select({ label, value, onChange, options, required, testId }) {
  return (
    <div>
      <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">{label}</label>
      <select required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function SubmitBtn({ children, variant }) {
  const bg = variant === "emerald" ? "bg-emerald-600 hover:bg-emerald-700"
           : variant === "rose" ? "bg-rose-600 hover:bg-rose-700"
           : variant === "blue" ? "bg-blue-600 hover:bg-blue-700"
           : variant === "violet" ? "bg-violet-600 hover:bg-violet-700"
           : "bg-[#1E2A9C] hover:bg-[#121A68]";
  return <button data-testid="submit" className={`w-full py-3 ${bg} text-white rounded-lg font-bold`}>{children}</button>;
}
